import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import Terminal from './components/Terminal';
import LoadingScreen from './components/LoadingScreen';

/*
  Edge-only barrel distortion mask:
  Black radial gradient at center (zero displacement there),
  fading to white at edges (full displacement there).
  This keeps center text sharp while warping the outer ring like CRT glass.
*/

function App() {
  // 'splash' → 'blank' → 'loading' → 'terminal'
  const [phase, setPhase] = useState('splash');
  const screenRef    = useRef(null);
  const idleAudioRef = useRef(null);

  /* ── Pre-load audio — do NOT play yet (needs user gesture first) ── */
  useEffect(() => {
    const audio = new Audio('/sounds/idle.mp3');
    audio.loop   = true;
    audio.volume = 0.18;
    idleAudioRef.current = audio;
    return () => { audio.pause(); audio.src = ''; };
  }, []);

  /* ── Boot: gesture → audio starts → 1 s blank → loading screen ─── */
  const boot = useCallback(() => {
    if (phase !== 'splash') return;
    idleAudioRef.current?.play().catch(() => {});
    setPhase('blank');
    setTimeout(() => setPhase('loading'), 1000);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'splash') return;
    window.addEventListener('keydown',   boot);
    window.addEventListener('mousedown', boot);
    return () => {
      window.removeEventListener('keydown',   boot);
      window.removeEventListener('mousedown', boot);
    };
  }, [phase, boot]);

  /* ── Jitter ──────────────────────────────────────────────────────── */
  useEffect(() => {
    let timer;
    const apply = () => {
      if (!screenRef.current) return;
      const x = (Math.random() - 0.5) * 2.5;
      const y = (Math.random() - 0.5) * 1.0;
      screenRef.current.style.transform = `translate(${x}px, ${y}px)`;
      setTimeout(() => { if (screenRef.current) screenRef.current.style.transform = ''; }, 55);
    };
    const schedule = () => {
      timer = setTimeout(() => { apply(); schedule(); }, 3500 + Math.random() * 8000);
    };
    schedule();
    return () => clearTimeout(timer);
  }, []);

  /* ── Horizontal sync glitch ──────────────────────────────────────── */
  useEffect(() => {
    let timer;
    const apply = () => {
      if (!screenRef.current) return;
      screenRef.current.classList.add('hsync-glitch');
      setTimeout(() => {
        if (screenRef.current) screenRef.current.classList.remove('hsync-glitch');
      }, 140 + Math.random() * 100);
    };
    const schedule = () => {
      timer = setTimeout(() => { apply(); schedule(); }, 12000 + Math.random() * 22000);
    };
    schedule();
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="App">

      {/*
        CRT filter pipeline:
          ① Edge-only barrel distortion — centre stays sharp, outer ring warps
          ② RGB chromatic aberration — subtle red/blue channel split
      */}
      <svg style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
        <defs>
          <filter id="crt-effects" x="-8%" y="-8%" width="116%" height="116%">

            {/* ① Distort the full image with smooth turbulence */}
            <feTurbulence type="fractalNoise" baseFrequency="0.012 0.01"
              numOctaves="1" seed="4" result="noise" />
            <feDisplacementMap in="SourceGraphic" in2="noise"
              scale="30" xChannelSelector="R" yChannelSelector="G"
              result="warped" />

            {/* ① Keep original image only at centre */}
            <feComposite in="SourceGraphic" in2="center-alpha"
              operator="in" result="original-center" />

            {/* ① Merge: warped edges (bottom) + clean centre (top) */}
            <feMerge result="combined">
              <feMergeNode in="warped-edges" />
              <feMergeNode in="original-center" />
            </feMerge>

            {/* ② RGB shift — red channel left, blue channel right */}
            <feColorMatrix type="matrix"
              values="1 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 1 0"
              in="combined" result="r-ch" />
            <feOffset in="r-ch" dx="-1.5" dy="0" result="r-off" />

            <feColorMatrix type="matrix"
              values="0 0 0 0 0  0 0 0 0 0  0 0 1 0 0  0 0 0 1 0"
              in="combined" result="b-ch" />
            <feOffset in="b-ch" dx="1.5" dy="0" result="b-off" />

            <feBlend in="r-off" in2="combined" mode="screen" result="rb" />
            <feBlend in="b-off" in2="rb"       mode="screen" />

          </filter>
        </defs>
      </svg>

      <div className="monitor-housing">
        <div className="monitor-bezel">

          <div className="monitor-screen-wrapper">
            <div className="monitor-screen" ref={screenRef}>

              <div className="crt-scanlines" aria-hidden="true" />
              <div className="crt-vignette"  aria-hidden="true" />
              <div className="crt-glass"     aria-hidden="true" />

              {phase === 'splash' && (
                <div className="splash-screen">
                  <div className="splash-content">
                    <div className="splash-logo">ibrahim@portfolio</div>
                    <div className="splash-sub">Last updated 3-15-2026</div>
                    <div className="splash-sep" />
                    <div className="splash-prompt">
                      PRESS ANY KEY TO INITIALIZE<span className="splash-cursor">_</span>
                    </div>
                  </div>
                </div>
              )}

              {/* blank — black screen for 1 s before boot sequence */}
              {phase === 'blank' && <div className="blank-screen" />}

              {phase === 'loading' && (
                <LoadingScreen onComplete={() => setPhase('terminal')} />
              )}

              {phase === 'terminal' && (
                <Terminal />
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
